// Hardware Leases (IT Budget, SPEC.md Module 5): every leased device under its
// lease, with its current user, OIC and share of the monthly payment, grouped
// by OIC then lease, or by lease. Finance views it; IT edits the lease amount.

// REACT IMPORTS
import React, { useCallback, useContext, useEffect, useState } from "react";

// MSAL
import { useMsal } from "@azure/msal-react";

// MUI COMPONENTS
import { Button, Checkbox, CircularProgress, IconButton, ListItemText, MenuItem, Snackbar, Alert, Table, TableBody, TableCell, TableHead, TableRow, TextField, ToggleButton, ToggleButtonGroup, Tooltip } from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import EventBusyIcon from "@mui/icons-material/EventBusy";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";

// CUSTOM COMPONENTS
import LeaseEditDialog from "./LeaseEditDialog";

// CONTEXT, API, CONSTANTS
import { UserContext } from "../../../context/UserContext";
import { getAPICall, getBlobAPICall, postAPICall } from "../../../config/apiCalls";
import { AMBER, GRAY, RED, apiError, fmtDay, fmtUSD, hasAccess, pillStyle } from "../itBudgetUtils";

// CSS
import "../ITBudget.css";

const STATUS_TABS = [
	{ id: "active", label: "Active" },
	{ id: "ending", label: "Ending in 90 Days" },
	{ id: "expired", label: "Expired" },
	{ id: "all", label: "All" },
];
// OIC chips, from the mockup
export const OIC_PILLS = {
	WA: { fg: "#184c93", bg: "#e7eef7" },
	OR: { fg: "#8a5a16", bg: "#fbeed7" },
	UT: { fg: "#2e7d32", bg: "#edf7ed" },
};
const DEVICE_STATUS_PILLS = { "Lease - Returned": GRAY, "Lease - EOL'ed": GRAY, "Lease - Wiped": GRAY, Retired: GRAY };

const oicPill = (oic) => <span style={pillStyle(OIC_PILLS[oic] || GRAY)}>{oic || "—"}</span>;
const byOicText = (counts) =>
	Object.entries(counts || {})
		.sort(([a], [b]) => a.localeCompare(b))
		.map(([oic, n]) => `${oic} ${n}`)
		.join(" · ");

const LeaseHeader = ({ lease, colSpan, canEdit, onEdit, here }) => (
	<TableRow className="itBudgetLeaseRow">
		<TableCell colSpan={colSpan}>
			<div className="itBudgetLeaseHeader">
				<span className="itBudgetMono" style={{ fontWeight: 700 }}>
					{lease.lease_number}
				</span>
				<span>{lease.lessor || <span className="itBudgetMuted">No lessor</span>}</span>
				<span>
					{fmtDay(lease.origin_date)} – {fmtDay(lease.term_date)}
					{lease.lease_length ? ` · ${lease.lease_length} mo` : ""}
				</span>
				{lease.status === "expired" ? (
					<span style={pillStyle(GRAY)}>Expired</span>
				) : (
					<span className={lease.status === "ending" ? "" : "itBudgetMuted"}>
						{lease.status === "ending" && <span style={{ ...pillStyle(AMBER), marginRight: 6 }}>Ending soon</span>}
						{lease.months_left} months left
					</span>
				)}
				<span>
					Monthly <strong>{lease.lease_amount === null ? <span style={{ color: RED.fg }}>Not entered</span> : fmtUSD(lease.lease_amount)}</strong>
				</span>
				<span className="itBudgetMuted">
					{here !== undefined ? `${here} of ${lease.device_count} devices here` : `${lease.device_count} devices`}
					{lease.share_per_device !== null && ` · ${fmtUSD(lease.share_per_device)} each`}
				</span>
				{canEdit && (
					<Tooltip title="Edit Lease">
						<IconButton size="small" onClick={() => onEdit(lease)} style={{ marginLeft: "auto" }}>
							<EditIcon fontSize="small" />
						</IconButton>
					</Tooltip>
				)}
			</div>
		</TableCell>
	</TableRow>
);

const DeviceRow = ({ device, showOic }) => (
	<TableRow style={device.counted ? undefined : { opacity: 0.6 }}>
		<TableCell className="itBudgetMono">{device.device || "—"}</TableCell>
		<TableCell>{device.model || "—"}</TableCell>
		<TableCell className="itBudgetMono">{device.service_tag || "—"}</TableCell>
		<TableCell>{device.employee || <span className="itBudgetMuted">Unassigned</span>}</TableCell>
		{showOic && <TableCell>{oicPill(device.oic)}</TableCell>}
		<TableCell>{device.status ? <span style={pillStyle(DEVICE_STATUS_PILLS[device.status] || { fg: "#184c93", bg: "#e8f0fe" })}>{device.status}</span> : "—"}</TableCell>
		<TableCell align="right">{device.counted ? fmtUSD(device.monthly_share) : <span className="itBudgetMuted">Not counted</span>}</TableCell>
	</TableRow>
);

const HardwareLeases = ({ access }) => {
	const { instance, accounts } = useMsal();
	const employeeID = useContext(UserContext);
	const canEdit = hasAccess(access, "leases", "full");

	const [status, setStatus] = useState("active");
	const [oic, setOic] = useState("");
	const [deviceStatus, setDeviceStatus] = useState([]); // device status display names; none selected = all
	const [search, setSearch] = useState("");
	const [searchText, setSearchText] = useState("");
	const [groupBy, setGroupBy] = useState("oic");
	const [data, setData] = useState(null);
	const [loading, setLoading] = useState(true);
	const [error, setError] = useState("");
	const [editing, setEditing] = useState(null);
	const [toast, setToast] = useState("");
	const [exporting, setExporting] = useState(false);
	// OIC groups start collapsed, so the page opens on the cost per office; a click shows the leases and devices.
	const [openOics, setOpenOics] = useState(() => new Set());
	const isOpen = (key) => openOics.has(key || "none");
	const toggleOic = (key) =>
		setOpenOics((prev) => {
			const next = new Set(prev);
			next.has(key || "none") ? next.delete(key || "none") : next.add(key || "none");
			return next;
		});

	useEffect(() => {
		postAPICall(instance, accounts[0], "/api/logs/access/add", {
			page_name: "IT Budget - Hardware Leases",
			employeeID: employeeID,
			notes: "Accessed the IT Budget Hardware Leases page",
		}).catch(() => {});
	}, []);

	// Search runs a moment after typing stops.
	useEffect(() => {
		const t = setTimeout(() => setSearch(searchText), 300);
		return () => clearTimeout(t);
	}, [searchText]);

	const load = useCallback(async () => {
		setLoading(true);
		setError("");
		try {
			const res = await getAPICall(instance, accounts[0], "/api/it-budget/leases", { status, oic, device_status: deviceStatus.join(","), search, group_by: groupBy });
			setData(res.data);
		} catch (err) {
			setError(apiError(err, "Couldn't load Hardware Leases."));
		} finally {
			setLoading(false);
		}
	}, [instance, accounts, status, oic, deviceStatus, search, groupBy]);

	useEffect(() => {
		load();
	}, [load]);

	// Downloads the leases on screen (same tab and filters) as an Excel workbook.
	const exportExcel = async () => {
		setExporting(true);
		setError("");
		try {
			const blob = await getBlobAPICall(instance, accounts[0], "/api/it-budget/leases/export", { status, oic, device_status: deviceStatus.join(","), search });
			const url = URL.createObjectURL(blob);
			const a = document.createElement("a");
			a.href = url;
			a.download = `Hardware Leases ${data?.today || new Date().toISOString().slice(0, 10)}.xlsx`;
			a.click();
			URL.revokeObjectURL(url);
		} catch (err) {
			setError(apiError(err, "Couldn't export Hardware Leases."));
		} finally {
			setExporting(false);
		}
	};

	const kpis = data?.kpis;
	const groups = data?.groups || [];
	// Render by the grouping the data came back in: after the switch is flipped, the old rows stay until the new ones arrive.
	const shownGroupBy = data?.group_by || groupBy;
	const showOic = shownGroupBy === "lease";
	const colSpan = showOic ? 7 : 6;
	const endingFirst = kpis?.ending_soon_first;
	// The lease for the ending banner, from whatever the current view holds.
	const leaseByNumber = (number) => {
		for (const g of groups) {
			if (g.lease_number === number) return g;
			for (const l of g.leases || []) if (l.lease_number === number) return l;
		}
		return null;
	};

	return (
		<div className="itBudgetPage">
			<div className="itBudgetHeader">
				<div>
					<div className="itBudgetBreadcrumb">IT Budget / Hardware Leases</div>
					<div className="itBudgetTitle">Hardware Leases</div>
					<div className="itBudgetSubtitle">Leased devices by OIC and lease, with each device's share of the monthly payment. Dell leases carry no sales tax.</div>
				</div>
				<Tooltip title="Download the leases shown (this tab and filters): cost by OIC, leases and devices">
					<span>
						<Button variant="contained" onClick={exportExcel} disabled={exporting || !data} startIcon={exporting ? <CircularProgress size={16} color="inherit" /> : <FileDownloadIcon />}>
							Export to Excel
						</Button>
					</span>
				</Tooltip>
			</div>

			{endingFirst && (
				<div className="itBudgetBanner" style={{ background: AMBER.bg, alignItems: "center" }}>
					<EventBusyIcon style={{ color: AMBER.fg }} />
					<div style={{ flex: 1 }}>
						{kpis.ending_soon === 1 ? "Lease " : `${kpis.ending_soon} leases end within ${kpis.warning_days} days; the first, `}
						<span className="itBudgetMono">{endingFirst.lease_number}</span>, ends {fmtDay(endingFirst.term_date)}. Confirm the return with the lessor so the payment stops.
					</div>
					<button className="itBudgetTab" onClick={() => setStatus("ending")}>
						Show
					</button>
					{canEdit && leaseByNumber(endingFirst.lease_number) && (
						<button className="itBudgetTab" onClick={() => setEditing(leaseByNumber(endingFirst.lease_number))}>
							Open Lease
						</button>
					)}
				</div>
			)}

			{/* KPI cards */}
			<div className="itBudgetKpiGrid">
				{[
					{ label: "Active Leases", value: kpis?.active_leases, sub: kpis?.lessors?.join(", ") || "No lessor entered" },
					{ label: "Leased Devices", value: kpis?.leased_devices, sub: byOicText(kpis?.leased_devices_by_oic) || "—" },
					{
						label: "Monthly Lease Cost",
						value: kpis ? fmtUSD(kpis.monthly_cost) : undefined,
						sub: kpis?.amount_not_entered ? `${kpis.amount_not_entered} lease${kpis.amount_not_entered === 1 ? " has" : "s have"} no amount yet` : "All amounts entered",
					},
					{
						label: `Ending in ${kpis?.warning_days ?? 90} Days`,
						value: kpis?.ending_soon,
						sub: endingFirst ? `${endingFirst.lease_number} · ${fmtDay(endingFirst.term_date)}` : "None",
						color: kpis?.ending_soon ? AMBER.fg : undefined,
					},
					{ label: "Amount Not Entered", value: kpis?.amount_not_entered, sub: "Enter from the Dell invoice", color: kpis?.amount_not_entered ? RED.fg : undefined },
				].map((card) => (
					<div className="itBudgetCard" key={card.label}>
						<div className="itBudgetOverline">{card.label}</div>
						<div className="itBudgetKpiValue" style={{ color: card.color }}>
							{card.value ?? "—"}
						</div>
						<div className="itBudgetMuted">{card.sub}</div>
					</div>
				))}
			</div>

			<div className="itBudgetCard" style={{ display: "flex", flexDirection: "column", gap: 12 }}>
				<div className="itBudgetTabs">
					{STATUS_TABS.map((t) => (
						<button key={t.id} className={`itBudgetTab${status === t.id ? " itBudgetTab--active" : ""}`} onClick={() => setStatus(t.id)}>
							{t.id === "ending" ? `Ending in ${kpis?.warning_days ?? 90} Days` : t.label}
						</button>
					))}
				</div>
				<div className="itBudgetFilters">
					<TextField select size="small" label="OIC" value={oic} onChange={(e) => setOic(e.target.value)} SelectProps={{ displayEmpty: true }} InputLabelProps={{ shrink: true }}>
						<MenuItem value="">All OICs</MenuItem>
						{Object.keys(OIC_PILLS).map((o) => (
							<MenuItem key={o} value={o}>
								{o}
							</MenuItem>
						))}
					</TextField>
					<TextField
						select
						size="small"
						label="Device Status"
						value={deviceStatus}
						onChange={(e) => {
							const picked = typeof e.target.value === "string" ? e.target.value.split(",") : e.target.value;
							setDeviceStatus(picked.includes("__clear") ? [] : picked);
						}}
						SelectProps={{
							multiple: true,
							displayEmpty: true,
							renderValue: (selected) => (selected.length ? selected.join(", ") : "All Statuses"),
							MenuProps: { PaperProps: { style: { maxHeight: 420 } } },
						}}
						InputLabelProps={{ shrink: true }}
						style={{ minWidth: 200, maxWidth: 320 }}
					>
						{(data?.device_statuses || []).map((name) => (
							<MenuItem key={name} value={name} dense>
								<Checkbox size="small" checked={deviceStatus.includes(name)} style={{ padding: 4, marginRight: 6 }} />
								<ListItemText primary={name} />
							</MenuItem>
						))}
						{deviceStatus.length > 0 && (
							<MenuItem value="__clear" dense>
								<ListItemText primary="Clear selection" primaryTypographyProps={{ color: "primary" }} />
							</MenuItem>
						)}
					</TextField>
					<TextField size="small" label="Search" placeholder="Device, user or lease #" value={searchText} onChange={(e) => setSearchText(e.target.value)} InputLabelProps={{ shrink: true }} />
					<div style={{ display: "flex", alignItems: "center", gap: 8, marginLeft: "auto" }}>
						{shownGroupBy === "oic" && groups.length > 0 && (
							<button
								className="itBudgetTab"
								onClick={() => setOpenOics(openOics.size >= groups.length ? new Set() : new Set(groups.map((g) => g.oic || "none")))}
							>
								{openOics.size >= groups.length ? "Collapse All" : "Expand All"}
							</button>
						)}
						<span className="itBudgetOverline">Group By</span>
						<ToggleButtonGroup size="small" exclusive color="primary" value={groupBy} onChange={(e, v) => v && setGroupBy(v)}>
							<ToggleButton value="oic">OIC → Lease</ToggleButton>
							<ToggleButton value="lease">Lease</ToggleButton>
						</ToggleButtonGroup>
					</div>
				</div>

				{error && <div className="itBudgetError">{error}</div>}
				{loading && !data ? (
					<div className="itBudgetEmpty">
						<CircularProgress />
					</div>
				) : !groups.length ? (
					<div className="itBudgetEmpty">No leases match these filters.</div>
				) : (
					<div className="itBudgetTableContainer" style={loading ? { opacity: 0.6 } : undefined}>
						<Table size="small" stickyHeader>
							<TableHead>
								<TableRow>
									<TableCell>Device Name</TableCell>
									<TableCell>Model</TableCell>
									<TableCell>Service Tag</TableCell>
									<TableCell>Assigned To</TableCell>
									{showOic && <TableCell>OIC</TableCell>}
									<TableCell>Status</TableCell>
									<TableCell align="right">Monthly Share</TableCell>
								</TableRow>
							</TableHead>
							<TableBody>
								{shownGroupBy === "oic"
									? groups.map((g) => (
											<React.Fragment key={g.oic || "none"}>
												<TableRow className="itBudgetOicRow" onClick={() => toggleOic(g.oic)}>
													<TableCell colSpan={colSpan}>
														<div className="itBudgetOicSummary">
															{isOpen(g.oic) ? <ExpandLessIcon /> : <ExpandMoreIcon />}
															<span style={{ ...pillStyle(OIC_PILLS[g.oic] || GRAY), fontSize: 13, height: 24, padding: "0 11px" }}>{g.oic || "—"}</span>
															<span>{g.office}</span>
															<span className="itBudgetMuted" style={{ fontSize: 13 }}>
																{g.leases.length} lease{g.leases.length === 1 ? "" : "s"}
															</span>
															<span style={{ marginLeft: "auto" }}>{g.device_count} devices</span>
															<span className="itBudgetOicCost">{fmtUSD(g.monthly_cost)} / month</span>
														</div>
													</TableCell>
												</TableRow>
												{isOpen(g.oic) && g.leases.map((l) => (
													<React.Fragment key={l.id}>
														<LeaseHeader lease={l} colSpan={colSpan} canEdit={canEdit} onEdit={setEditing} here={l.devices_here} />
														{l.devices.map((d) => (
															<DeviceRow key={d.device_id} device={d} showOic={false} />
														))}
													</React.Fragment>
												))}
											</React.Fragment>
									  ))
									: null}
								{shownGroupBy === "oic" && groups.length > 1 && (
									<TableRow className="itBudgetOicRow itBudgetOicTotal">
										<TableCell colSpan={colSpan}>
											<div className="itBudgetOicSummary">
												<span>Total</span>
												<span style={{ marginLeft: "auto" }}>{groups.reduce((n, g) => n + g.device_count, 0)} devices</span>
												<span className="itBudgetOicCost">{fmtUSD(groups.reduce((t, g) => t + Math.round(g.monthly_cost * 100), 0) / 100)} / month</span>
											</div>
										</TableCell>
									</TableRow>
								)}
								{shownGroupBy === "lease"
									? groups.map((l) => (
											<React.Fragment key={l.id}>
												<LeaseHeader lease={l} colSpan={colSpan} canEdit={canEdit} onEdit={setEditing} />
												{l.devices.length ? (
													l.devices.map((d) => <DeviceRow key={d.device_id} device={d} showOic />)
												) : (
													<TableRow>
														<TableCell colSpan={colSpan} className="itBudgetMuted">
															No devices linked to this lease.
														</TableCell>
													</TableRow>
												)}
											</React.Fragment>
									  ))
									: null}
							</TableBody>
						</Table>
					</div>
				)}
				<div className="itBudgetMuted">
					A device counts on a lease unless it's Lease - Returned, Lease - EOL'ed, Lease - Wiped or Retired; each counted device's share is the monthly amount ÷ counted devices. A device's OIC is its user's OIC, or its office when unassigned.
				</div>
			</div>

			{editing && (
				<LeaseEditDialog
					lease={editing}
					budgetItems={data?.budget_items || []}
					lessors={kpis?.lessors || []}
					onClose={() => setEditing(null)}
					onSaved={() => {
						setToast(`Lease ${editing.lease_number} saved.`);
						setEditing(null);
						load();
					}}
				/>
			)}
			<Snackbar open={Boolean(toast)} autoHideDuration={4000} onClose={() => setToast("")} anchorOrigin={{ vertical: "bottom", horizontal: "center" }}>
				<Alert severity="success" onClose={() => setToast("")}>
					{toast}
				</Alert>
			</Snackbar>
		</div>
	);
};

export default HardwareLeases;
